import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import Breadcrumbs from "../components/Breadcrumbs";
import DumpsterCard from "../components/DumpsterCard";
import FAQAccordion from "../components/FAQAccordion";
import CTASection from "../components/CTASection";
import { useRequestModal } from "../components/RequestModal";
import { FRONTLOAD_SIZES, ROLLOFF_SIZES } from "../data/site";

export default function SizeGuide() {
  const { openRequest } = useRequestModal();
  return (
    <>
      <SEO
        title="Dumpster Size Guide"
        description="Compare 20, 30 & 40 yard roll-off dumpsters and 3–8 yard front-load sizes. Find the right dumpster for your Yavapai County project."
        path="/dumpster-size-guide"
      />
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Dumpster Size Guide" }]} />
          <div className="mt-8 max-w-3xl">
            <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl text-slate-900 leading-[1.05]">Dumpster Size Guide</h1>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              Every roll-off and front-load size we run, what it's best for, and how much it holds. Still unsure? Tell us what you're working on and we'll help you choose.
            </p>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-16 bg-slate-50" data-testid="sizeguide-rolloff">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">Roll-Off Sizes</h2>
            <p className="text-slate-600 mt-2">For construction, contractors & one-time projects. No maximum rental period.</p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl">
            {ROLLOFF_SIZES.map((s, i) => (
              <Reveal key={s.size} delay={i * 0.08}>
                <DumpsterCard {...s} />
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-12">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="comparison-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <caption className="sr-only">Roll-off dumpster size comparison</caption>
                  <thead>
                    <tr className="bg-ink text-white text-left">
                      <th scope="col" className="px-5 py-3.5 font-heading font-semibold">Size</th>
                      <th scope="col" className="px-5 py-3.5 font-heading font-semibold">Best For</th>
                      <th scope="col" className="px-5 py-3.5 font-heading font-semibold">Relative Capacity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ROLLOFF_SIZES.map((s) => (
                      <tr key={s.size} className="border-t border-slate-100 hover:bg-sky-50/50 transition-colors">
                        <th scope="row" className="px-5 py-4 font-heading font-bold text-slate-900 whitespace-nowrap">{s.label}</th>
                        <td className="px-5 py-4 text-slate-600">{s.uses.join(", ")}</td>
                        <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{s.capacity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-14 sm:py-16 bg-white border-t border-slate-200" data-testid="sizeguide-frontload">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">Front-Load Sizes</h2>
            <p className="text-slate-600 mt-2">For recurring residential & commercial waste service.</p>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {FRONTLOAD_SIZES.map((s, i) => (
              <Reveal key={s.size} delay={i * 0.06}>
                <div className="rounded-xl border border-slate-200 p-5 text-center hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={`frontload-size-${s.size}`}>
                  <span className="font-heading font-extrabold text-4xl text-sky-600">{s.size}</span>
                  <p className="font-heading font-semibold text-slate-900 mt-1">Yard</p>
                  {s.specs && (
                    <p className="text-xs font-semibold text-slate-500 mt-2" data-testid={`frontload-size-${s.size}-dimensions`}>{s.specs.dimensions}</p>
                  )}
                  <p className="text-xs text-slate-500 mt-2">{s.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FAQAccordion page="sizeguide" />
          <Reveal className="mt-10">
            <div className="bg-sky-50 border border-sky-100 rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4 max-w-3xl mx-auto">
              <p className="text-slate-700 text-center sm:text-left">
                <span className="font-heading font-bold text-slate-900">Not sure what size you need?</span> We'll help you choose — that's part of the service.
              </p>
              <button onClick={() => openRequest("Not Sure — Help Me Choose")} className="shrink-0 px-6 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-heading font-semibold text-sm transition-all active:scale-[0.98]" data-testid="sizeguide-help-me-choose">
                Help Me Choose
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection title="Know Your Size? Let's Get It Delivered." />
    </>
  );
}
